/**
 * Stable string codes used on every API error response. The frontend maps each one to a
 * user-facing Russian message (frontend/src/shared/api/errorToMessage.ts). Extended as new
 * failure modes are introduced — grouped roughly by the plan section that introduces them.
 */
export const ErrorCode = {
  // Generic (stage 0)
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  UNPROCESSABLE_ENTITY: 'UNPROCESSABLE_ENTITY',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  INTERNAL_ERROR: 'INTERNAL_ERROR',

  // Store context / auth (§1, §3.8, этап 1.2)
  STORE_HEADER_REQUIRED: 'STORE_HEADER_REQUIRED',
  MEMBERSHIP_NOT_APPROVED: 'MEMBERSHIP_NOT_APPROVED',
  FORBIDDEN_ROLE: 'FORBIDDEN_ROLE',
  PASSWORD_CHANGE_REQUIRED: 'PASSWORD_CHANGE_REQUIRED',
  LOGIN_TAKEN: 'LOGIN_TAKEN',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',

  // Stores & join requests (§3, §3.7)
  STORE_CODE_NOT_FOUND: 'STORE_CODE_NOT_FOUND',
  JOIN_PAUSED: 'JOIN_PAUSED',
  JOIN_ATTEMPTS_EXCEEDED: 'JOIN_ATTEMPTS_EXCEEDED',
  JOIN_REQUEST_PENDING: 'JOIN_REQUEST_PENDING',
  JOIN_REAPPLY_TOO_SOON: 'JOIN_REAPPLY_TOO_SOON',
  ALREADY_MEMBER: 'ALREADY_MEMBER',
  COUNTERPARTY_ALREADY_LINKED: 'COUNTERPARTY_ALREADY_LINKED',

  // Categories (§2.1)
  CATEGORY_NAME_TAKEN: 'CATEGORY_NAME_TAKEN',
  CATEGORY_DEPTH_EXCEEDED: 'CATEGORY_DEPTH_EXCEEDED',
  CATEGORY_PCS_PER_BOX_LOCKED: 'CATEGORY_PCS_PER_BOX_LOCKED',
  CATEGORY_DELETE_FORBIDDEN: 'CATEGORY_DELETE_FORBIDDEN',
  CATEGORY_HAS_CHILDREN: 'CATEGORY_HAS_CHILDREN',
  CATEGORY_ARCHIVED: 'CATEGORY_ARCHIVED',
  CATEGORY_GROUP_NOT_EMPTY: 'CATEGORY_GROUP_NOT_EMPTY',
  CATEGORY_GROUP_ARCHIVED: 'CATEGORY_GROUP_ARCHIVED',

  // Counterparties (§2.2)
  COUNTERPARTY_HAS_DOCUMENTS: 'COUNTERPARTY_HAS_DOCUMENTS',
  COUNTERPARTY_ARCHIVED: 'COUNTERPARTY_ARCHIVED',
  MERGE_BOTH_HAVE_ACCOUNTS: 'MERGE_BOTH_HAVE_ACCOUNTS',
  MERGE_SAME: 'MERGE_SAME',

  // Documents / lifecycle (§8)
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
  DOCUMENT_INCOMPLETE: 'DOCUMENT_INCOMPLETE',
  EMPTY_DOCUMENT: 'EMPTY_DOCUMENT',
  STATE_VERSION_MISMATCH: 'STATE_VERSION_MISMATCH',
  RECALC_PENDING: 'RECALC_PENDING',
  LOCK_TIMEOUT: 'LOCK_TIMEOUT',
  CONCURRENT_UPDATE: 'CONCURRENT_UPDATE',
  TRANSACTION_TIMEOUT: 'TRANSACTION_TIMEOUT',
  INVARIANT_VIOLATION: 'INVARIANT_VIOLATION',
  QTY_OUT_OF_RANGE: 'QTY_OUT_OF_RANGE',
  LINE_REFERENCED_BY_RETURN: 'LINE_REFERENCED_BY_RETURN',

  // Payments (§7)
  ALLOCATION_EXCEEDS_PAYMENT: 'ALLOCATION_EXCEEDS_PAYMENT',
  ALLOCATION_EXCEEDS_DEBT: 'ALLOCATION_EXCEEDS_DEBT',
  ALLOCATION_WRONG_DIRECTION: 'ALLOCATION_WRONG_DIRECTION',
  OFFSET_SIDES_MISMATCH: 'OFFSET_SIDES_MISMATCH',
  PAYMENT_LINKED_TO_DOCUMENT: 'PAYMENT_LINKED_TO_DOCUMENT',

  // Order requests (§10)
  ORDER_REQUEST_ALREADY_CONVERTED: 'ORDER_REQUEST_ALREADY_CONVERTED',

  // Attachments (§13)
  ATTACHMENT_LIMIT_PER_DOCUMENT: 'ATTACHMENT_LIMIT_PER_DOCUMENT',
  ATTACHMENT_TOO_LARGE: 'ATTACHMENT_TOO_LARGE',
  ATTACHMENT_QUOTA_EXCEEDED: 'ATTACHMENT_QUOTA_EXCEEDED',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];
